import {
  renderSelectedOption,
  createPlaceholderHtml,
  createOptionsListHtml
} from './templates';
import { updateAsyncTaskGanttV2 } from '../../GanttVisualization.helper';
import {
  getSessionTokenData,
  getSignedUser
} from '../../../../utils/userUtils';
import { notificationService } from '../../../../services';
import { sentryLogger } from '../../../../monitor/monitor';

export const UI_ELEMENTS = {
  EMPTY_SELECTION: `<div class="empty-selection">
         <div class="plus-icon-container"></div>
       </div>`,
  INDEX_DESCRIPTION: 2
};

const initializeResponsibleCallbacks = () => {
  window.responsibleCallbacks = window.responsibleCallbacks || {};
};

const updateResponsibleUI = (options) => {
  const { taskId, columnName, responsables, column, optionsList } = options;
  const placeholderId = `responsible-placeholder-${columnName}-${taskId}`;
  const placeholderEl = document.getElementById(placeholderId);

  if (placeholderEl) {
    requestAnimationFrame(() => {
      placeholderEl.innerHTML =
        responsables.length > 0
          ? renderSelectedOption(responsables, column)
          : UI_ELEMENTS.EMPTY_SELECTION;
    });
  }

  const optionsListEl = document.querySelector(
    `[data-task-id="${taskId}"] .options-list`
  );

  if (optionsListEl && optionsList) {
    const createBtn = optionsListEl.querySelector(
      '[data-action="responsibleCreate"]'
    )?.parentElement;
    const removeBtn = optionsListEl.querySelector(
      '[data-action="responsibleRemove"]'
    )?.parentElement;

    const newOptionsListHtml = createOptionsListHtml(optionsList, responsables);

    optionsListEl.innerHTML = '';
    if (createBtn) optionsListEl.appendChild(createBtn);

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newOptionsListHtml;
    while (tempDiv.firstChild) {
      optionsListEl.appendChild(tempDiv.firstChild);
    }

    if (removeBtn) optionsListEl.appendChild(removeBtn);
  }
};

const assignSubByResponsible = (task) => {
  const sessionTokenData = getSessionTokenData();
  const availableRolesToUseThisFeature = [
    'superadmin',
    'admin',
    'projectleader',
    'planner',
    'superintendent'
  ];

  if (!availableRolesToUseThisFeature.includes(sessionTokenData.role)) return;
  let toAssignSub = null;

  if (task.responsables && task.responsables.length) {
    for (let i = 0; i < task.responsables.length; i++) {
      const currentSubUser = task.responsables[i].subcontractId;
      if (currentSubUser) {
        toAssignSub = currentSubUser;
        break;
      }
    }
  }
  if (toAssignSub) {
    task.subcontractId = toAssignSub;
    return true;
  }
  return false;
};

const updateResponsiblesList = (task, newResponsible) => {
  if (!Array.isArray(task.responsables)) {
    task.responsables = [];
  }

  const responsibleIndex = task.responsables.findIndex(
    (responsible) => String(responsible.id) === String(newResponsible.id)
  );

  if (responsibleIndex === -1) {
    task.responsables.push(newResponsible);
  } else {
    task.responsables.splice(responsibleIndex, 1);
  }

  return [...task.responsables];
};

const handleLookaheadUpdates = (
  task,
  taskFromLookahead,
  responsibleUpdated
) => {
  if (responsibleUpdated && taskFromLookahead) {
    taskFromLookahead.subcontractId = task.subcontractId;
  }
};

const executeCallbackIfNeeded = (task, newResponsible, callbackOnSelection) => {
  if (callbackOnSelection) {
    const selectedIds = task.responsables.map((responsible) => responsible.id);
    const dictionary = { [newResponsible.id]: newResponsible };
    callbackOnSelection(selectedIds, dictionary);
  }
};

const updateAsyncTaskIfNeeded = (
  taskFromLookahead,
  column,
  responsables,
  gantt
) => {
  if (taskFromLookahead) {
    updateAsyncTaskGanttV2(
      taskFromLookahead,
      column.name,
      responsables,
      gantt,
      true,
      false,
      false,
      true,
      true
    );
  }
};

const sendNotificationIfNeeded = async (task, column, oldResponsables) => {
  if (
    column.name === 'responsables' &&
    task.responsables.length > oldResponsables.length
  ) {
    const user = getSignedUser();
    const sector = JSON.parse(sessionStorage.getItem('currentSector'));
    task.sectorId = sector?.id;

    try {
      await notificationService.createConstraint({
        user,
        constraint: task,
        module: 'tasks',
        type_notification: 'assign_responsible',
        userId: task.responsables[task.responsables.length - 1].id
      });
    } catch (error) {
      sentryLogger.error('Failed to send notification:', error);
    }
  }
};

const handleResponsibleSelection = async (task, newResponsible, options) => {
  const { taskFromLookahead, column, gantt, callbackOnSelection } = options;

  const oldResponsables = [...(task.responsables || [])];
  task.responsables = updateResponsiblesList(task, newResponsible);

  const responsibleUpdated = assignSubByResponsible(task);
  handleLookaheadUpdates(task, taskFromLookahead, responsibleUpdated);

  updateResponsibleUI({
    taskId: task.id,
    columnName: column.name,
    responsables: task.responsables,
    column,
    optionsList: options.optionsList
  });

  executeCallbackIfNeeded(task, newResponsible, callbackOnSelection);
  updateAsyncTaskIfNeeded(taskFromLookahead, column, task.responsables, gantt);
  await sendNotificationIfNeeded(task, column, oldResponsables);
};

export const registerTaskCallbacks = (taskId, task, options) => {
  initializeResponsibleCallbacks();

  window.responsibleCallbacks = window.responsibleCallbacks || {};
  window.responsibleCallbacks[taskId] = {
    selection: (payload, dictionary) => {
      const newResponsible = dictionary[payload[0]];
      handleResponsibleSelection(task, newResponsible, options);
    }
  };
};

export const prepareUIElements = (options) => {
  const { selectedObject, optionsList, column, taskId } = options;
  const selectedResponsibles =
    selectedObject[column.name] || selectedObject.responsables || [];
  const objectId = taskId || selectedObject.id || 'default';

  const placeholderHtml = createPlaceholderHtml(
    column.name,
    objectId,
    selectedResponsibles,
    column
  );
  const optionsListHtml = createOptionsListHtml(
    optionsList,
    selectedResponsibles,
    column
  );

  return { placeholderHtml, optionsListHtml };
};

const parseResponsibleValue = (value) => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    const parts = value.split('-');
    const valueId = parts[0];
    const valueName = parts[1];
    // eslint-disable-next-line no-magic-numbers
    const valueLastname = parts[2] || '';

    sentryLogger.warn('Failed to parse responsible value:', error);

    return {
      id: valueId,
      name: valueName,
      lastname: valueLastname
    };
  }
};

const createResponsibleDictionary = (responsibleData) => {
  if (!responsibleData) return {};

  const dictionary = {};
  dictionary[responsibleData.id] = responsibleData;
  return dictionary;
};

const handleResponsibleAction = (action, target, taskId) => {
  const callbacks =
    window.responsibleCallbacks && window.responsibleCallbacks[taskId];
  if (!callbacks) return;

  const actionHandlers = {
    responsibleSelection: () => {
      const value = target.getAttribute('data-value');
      const responsibleData = parseResponsibleValue(value);

      if (!responsibleData) return;

      const listItem = target.closest('.dropdown-option');
      if (listItem) {
        listItem.classList.toggle('selected-option');
      }

      const dictionary = createResponsibleDictionary(responsibleData);

      if (callbacks.selection) {
        callbacks.selection([responsibleData.id], dictionary);
      }
    },
    responsibleCreate: () => callbacks.create && callbacks.create(),
    responsibleRemove: () => callbacks.remove && callbacks.remove()
  };

  const handler = actionHandlers[action];
  if (handler) handler();
};

const createResponsibleEventHandler = () => (event) => {
  const target = event.target.closest('[data-action]');
  if (!target) return;

  const action = target.getAttribute('data-action');
  const container = target.closest('[data-task-id]');
  if (!container) return;

  const taskId = container.getAttribute('data-task-id');
  handleResponsibleAction(action, target, taskId);
  event.stopPropagation();
};

export const setupGlobalHandlers = () => {
  initializeResponsibleCallbacks();

  if (window._responsibleGlobalHandler) return;

  const responsibleGlobalHandler = createResponsibleEventHandler();
  document.addEventListener('click', responsibleGlobalHandler);
  window._responsibleGlobalHandler = responsibleGlobalHandler;
};

export const cleanupResponsibleGlobalHandlers = () => {
  if (window._responsibleGlobalHandler) {
    document.removeEventListener('click', window._responsibleGlobalHandler);
    delete window._responsibleGlobalHandler;
  }
};

export const formatTagsToString = (tags, defaultValue = '') => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return defaultValue;
  }

  const tagNames = tags
    .map((tag) => `${tag.name || ''} ${tag.lastname || ''}`.trim())
    .filter(Boolean);

  return tagNames.length > 0 ? tagNames.join(', ') : defaultValue;
};
